"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Tabs = Tabs;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const cn_1 = require("../lib/cn");
const icon_1 = require("./icon");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Switches views inside one pane. Roving tabindex, arrow keys.                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **Roving tabindex, not eight tab stops.** ARIA's tablist pattern puts exactly one tab in
 * the page's tab order — the active one — and moves between them with arrow keys. Eight tab
 * stops in a row is what a naive implementation gives, and it makes a keyboard user press Tab
 * seven times to leave a four-tab switcher.
 *
 * ⚠️ **Focus moves through a REF, not `document.getElementById`.** The design system's
 * implementation ids each tab `tab-${value}` and looks it up globally. Two tab groups on one
 * screen with a shared value — and `list` / `grid` / `board` are the obvious shared values in
 * this product — produce duplicate ids, and arrowing in the second group focuses a tab in the
 * first. The refs are scoped to this instance and cannot collide.
 *
 * ⚠️ **The ids are still needed for `aria-controls`/`aria-labelledby`, so they carry a
 * `useId()` prefix** rather than being global names.
 */
function Tabs({ items, value, onValueChange, variant = 'pill', label, className, }) {
    const underline = variant === 'underline';
    const scope = (0, react_1.useId)();
    const buttons = (0, react_1.useRef)([]);
    const focusTab = (index) => {
        const item = items[index];
        if (item === undefined)
            return;
        onValueChange?.(item.value);
        buttons.current[index]?.focus();
    };
    const onKeyDown = (event, index) => {
        const last = items.length - 1;
        if (last < 0)
            return;
        switch (event.key) {
            case 'ArrowRight':
                event.preventDefault();
                /* Wraps, which the ARIA pattern allows and this product wants: four view tabs are a
                 * ring, not a line with ends. */
                focusTab(index === last ? 0 : index + 1);
                break;
            case 'ArrowLeft':
                event.preventDefault();
                focusTab(index === 0 ? last : index - 1);
                break;
            case 'Home':
                event.preventDefault();
                focusTab(0);
                break;
            case 'End':
                event.preventDefault();
                focusTab(last);
                break;
            default:
                break;
        }
    };
    return ((0, jsx_runtime_1.jsx)("div", { role: "tablist", "aria-label": label, "aria-orientation": "horizontal", className: (0, cn_1.cn)(
        /* ⚠️ 17 Oct 2026 · ONE line that scrolls on its own — eleven tabs on a phone wrapped their
           labels inside a 36px button, or pushed the page sideways. */
        underline
            ? 'scrollbar-none flex min-w-0 flex-nowrap items-center gap-1 overflow-x-auto overflow-y-hidden border-b'
            : 'bg-muted text-muted-foreground inline-flex h-8 w-fit items-center justify-center rounded-lg p-[3px]', className), children: items.map((item, index) => {
            const active = item.value === value;
            return ((0, jsx_runtime_1.jsxs)("button", { ref: (element) => {
                    buttons.current[index] = element;
                }, id: `${scope}-tab-${item.value}`, type: "button", role: "tab", "aria-selected": active, "data-state": active ? 'active' : 'inactive', 
                /* The roving part: only the active tab is reachable by Tab. */
                tabIndex: active ? 0 : -1, onKeyDown: (event) => {
                    onKeyDown(event, index);
                }, onClick: () => {
                    onValueChange?.(item.value);
                }, className: underline
                    ? (0, cn_1.cn)('focus-visible:ring-ring/50 -mb-px inline-flex h-9 shrink-0 items-center gap-2 rounded-t-sm border-b-2 px-3 text-[13px] font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-[3px]', active
                        ? 'border-primary text-foreground'
                        : 'text-muted-foreground hover:text-foreground border-transparent')
                    : (0, cn_1.cn)('focus-visible:ring-ring/50 inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-[3px]', active ? 'bg-background text-foreground shadow-sm' : 'hover:text-foreground'), children: [item.icon === undefined ? null : (0, jsx_runtime_1.jsx)(icon_1.Icon, { name: item.icon, size: underline ? 14 : 13 }), item.label, item.count === undefined ? null : ((0, jsx_runtime_1.jsx)("span", { className: "text-muted-foreground text-[11px] tabular-nums", children: item.count }))] }, item.value));
        }) }));
}
//# sourceMappingURL=tabs.js.map